import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Fetcher, HTTPError, formatPrice } from '@this/src/util';
import { Link } from '@this/shared/ui/navigations/link';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import { ButtonBase, Collapse } from '@material-ui/core';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import ExpandMore from '@material-ui/icons/ExpandMore';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import type Invoice from '@this/domain/invoice/invoice2';
import type {
  BugyoPostDealHistoryResponseArgs,
  BugyoPostDealHistoryStatus
} from '@this/domain/bugyo/bugyo_post_deal_history';
import BugyoPostDealHistory, {
  convertBugyoPostDealHistoryResponseToArgs
} from '@this/domain/bugyo/bugyo_post_deal_history';

const STATUS_INTERVAL = 3000;

export interface BugyoExternalProps {
  bugyoConnected: boolean;
  invoice: Invoice;
}

const getStyles = (theme: AITTheme) => ({
  horizontal: { display: 'flex', gap: 20 },
  ul: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 15,
    listStyleType: 'disc' as const,
    paddingInlineStart: 30
  },
  flexItem: { flex: 1 },
  submitArea: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 },
  reasonCell: { maxWidth: 320, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-all' as const },
  // 連携失敗の明細を開くトグル。表の行内に置くため素の見た目にする
  expandToggle: {
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 2,
    cursor: 'pointer' as const,
    font: 'inherit',
    textAlign: 'left' as const
  },
  expandIcon: { fontSize: 18 },
  expandIconOpen: { fontSize: 18, transform: 'rotate(180deg)' },
  // 展開行はセルの余白を消し、左の字下げと背景色で親の履歴に属することを示す
  detailCell: {
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 32,
    borderBottom: 'none',
    backgroundColor: theme.tokens.colors.background.disabled
  }
});

type HeaderCellProps = { children: React.ReactNode; width?: string; nowrap?: boolean };

// 共通 TableCell はヘッダに白背景を敷くが、freee 側の履歴表に合わせて背景色を出さない
const HeaderCell: React.FC<HeaderCellProps> = ({ children, width, nowrap }) => (
  <TableCell th width={width} nowrap={nowrap} style={{ backgroundColor: 'transparent' }}>
    {children}
  </TableCell>
);

const BugyoExternal: React.FC<BugyoExternalProps> = ({ bugyoConnected, invoice }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [histories, setHistories] = useState<BugyoPostDealHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // この画面で登録を実行したかどうか。過去の失敗理由を開き直すたびに出さないための判定に使う
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 連携失敗の明細は既定で閉じておき、開いた履歴のIDだけ保持する
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const year = invoice.from ? invoice.from.year() : undefined;
  const month = invoice.from ? invoice.from.month() + 1 : undefined;

  // silent はポーリングからの再取得用。表を消さずに裏側で最新化する
  const fetchHistories = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setLoading(true);
      try {
        const res = await Fetcher.get<{ histories: BugyoPostDealHistoryResponseArgs[] }>(
          '/organization/bugyo/histories',
          { year, month }
        );
        setHistories(
          res.histories.map(
            history => new BugyoPostDealHistory(convertBugyoPostDealHistoryResponseToArgs(history))
          )
        );
      } catch (e) {
        setErrorMessage('登録履歴の取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        if (!options?.silent) setLoading(false);
      }
    },
    [year, month]
  );

  useEffect(() => {
    if (bugyoConnected) fetchHistories();
  }, [bugyoConnected, fetchHistories]);

  // 処理中の履歴があるあいだはステータスAPIで完了を監視し、完了した時点で一覧を取り直す。
  // 一覧APIは伝票ログまで引くため、ポーリングでは使わない（freee と同じ方式）
  useEffect(() => {
    const processingHistory = histories.find(h => h.inProgress);
    if (!processingHistory) return undefined;

    const timer = setInterval(() => {
      Fetcher.get<{ status: BugyoPostDealHistoryStatus | null }>('/organization/bugyo/histories/status', {
        history_id: processingHistory.historyId
      })
        .then(res => {
          if (res.status === 'succeeded' || res.status === 'failed') {
            fetchHistories({ silent: true });
          }
        })
        .catch(() => {
          setErrorMessage('登録状況の取得に失敗しました。時間をおいて再度お試しください。');
        });
    }, STATUS_INTERVAL);
    return () => clearInterval(timer);
  }, [histories, fetchHistories]);

  // 登録処理中の履歴があるあいだは、同じ仕訳伝票が二重に登録されないよう再登録させない
  const processing = histories.some(h => h.inProgress);
  const latestHistory = histories[histories.length - 1];

  useEffect(() => {
    if (!submitted || processing) return;
    if (latestHistory?.errorMessage) setErrorMessage(latestHistory.errorMessage);
  }, [submitted, processing, latestHistory]);

  const toggleExpanded = (historyId: number) => {
    setExpandedIds(prev =>
      prev.includes(historyId) ? prev.filter(id => id !== historyId) : [...prev, historyId]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    // 前回の失敗理由を残さないよう、実行のたびにクリアして完了時に出し直す
    setSubmitted(false);
    setErrorMessage(null);
    try {
      await Fetcher.post('/organization/bugyo/deals', { invoice_id: invoice.id });
      setSubmitted(true);
    } catch (e) {
      const message = e instanceof HTTPError ? (e.response?.data?.message as string | undefined) : undefined;
      setErrorMessage(message ?? '登録に失敗しました。時間をおいて再度お試しください。');
      setSubmitting(false);
      return;
    }
    // 登録の成否とは別に扱いたいので、履歴の再取得は登録の try/catch の外で行う
    await fetchHistories();
    setSubmitting(false);
  };

  if (!bugyoConnected) {
    return (
      <p>
        勘定奉行と連携するには
        <Link href="/organization/setting">システム設定</Link>
        から連携してください。
      </p>
    );
  }

  return (
    <div style={styles.horizontal}>
      <ul style={styles.ul}>
        <li>
          <h3>請求書登録</h3>
          <p>請求内容を勘定奉行の「仕訳伝票」として明細ごとに登録できます。</p>
          {errorMessage && (
            <Alert variant="danger" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          {!loading && (
            <div style={styles.submitArea}>
              <Button
                onClick={handleSubmit}
                disabled={submitting || processing}
                loading={submitting || processing}
              >
                {histories.length > 0 ? '再登録' : '勘定奉行に取引を登録'}
              </Button>
              {processing && <span>登録処理中です。完了までお待ちください。</span>}
              {!processing && latestHistory && <span>{latestHistory.statusText}</span>}
            </div>
          )}
        </li>
      </ul>
      <div style={styles.flexItem}>
        {loading && <Loading />}
        {!loading && histories.length > 0 && (
          <TableContainer height="auto">
            <Table>
              <TableHead>
                <TableRow>
                  <HeaderCell>登録番号</HeaderCell>
                  <HeaderCell>実行種別</HeaderCell>
                  <HeaderCell>登録方法</HeaderCell>
                  <HeaderCell>ステータス</HeaderCell>
                  <HeaderCell>開始時刻</HeaderCell>
                  <HeaderCell>終了時刻</HeaderCell>
                  <HeaderCell>件数</HeaderCell>
                  <HeaderCell>失敗理由</HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {histories.map((h, i) => {
                  const expanded = expandedIds.includes(h.historyId);
                  return (
                    <React.Fragment key={h.historyId}>
                      <TableRow>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{h.actionTypeText}</TableCell>
                        <TableCell>{h.methodText}</TableCell>
                        <TableCell>{h.statusText}</TableCell>
                        <TableCell>{h.startedAtText}</TableCell>
                        <TableCell>{h.finishedAtText}</TableCell>
                        <TableCell width="80px" nowrap>
                          {h.countText}
                        </TableCell>
                        <TableCell style={styles.reasonCell}>
                          {h.errorMessage}
                          {h.failedLogs.length > 0 && (
                            <ButtonBase
                              onClick={() => toggleExpanded(h.historyId)}
                              style={styles.expandToggle}
                              {...{ component: 'div' }}
                            >
                              {`${h.failedLogs.length}件が受け入れられませんでした`}
                              <ExpandMore style={expanded ? styles.expandIconOpen : styles.expandIcon} />
                            </ButtonBase>
                          )}
                        </TableCell>
                      </TableRow>
                      {h.failedLogs.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={8} style={styles.detailCell}>
                            <Collapse in={expanded} unmountOnExit>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <HeaderCell>伝票No</HeaderCell>
                                    <HeaderCell>日付</HeaderCell>
                                    <HeaderCell>金額</HeaderCell>
                                    <HeaderCell>旅程ID</HeaderCell>
                                    <HeaderCell>失敗理由</HeaderCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {h.failedLogs.map(log => (
                                    <TableRow key={log.idx}>
                                      <TableCell>{log.idx + 1}</TableCell>
                                      <TableCell>{log.issueDate}</TableCell>
                                      <TableCell>{formatPrice(log.amount)}</TableCell>
                                      <TableCell>{log.refNumber}</TableCell>
                                      <TableCell style={styles.reasonCell}>{log.reason}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </div>
  );
};

export default BugyoExternal;
